import {Dimension, Pair, Point} from './abstract-units';
import {Cell, CellDisplayInfo, CellId} from './cell';
import {BOX_EDGES_MAP, BoxEdge, BoxEdges, Edge, EdgeDisplayInfo, EdgeId, EdgeSides, Side} from './edge';
import {Layout} from './layout';
import {Widget} from './widget/widget';

export interface EdgeSnapshot {
    edge: Edge; // Used for saving object reference during restoring.
    id: EdgeId;
    axis: Dimension;
    axisRange: Point;
    movingAxis: Dimension;
    movingAxisPos: number;
    sides: Pair<CellSnapshot[]>;
    isBoundary: boolean;
    display: EdgeDisplayInfo;
}

export interface CellSnapshot {
    cell: Cell; // Used for saving object reference during restoring.
    id: CellId;
    edges: [EdgeSnapshot, EdgeSnapshot, EdgeSnapshot, EdgeSnapshot];
    widgets: Widget[];
    activeWidget: Widget;
    display: CellDisplayInfo;
}

export class LayoutSnapshot {

    public size: Point;
    public edges: EdgeSnapshot[];
    public cells: CellSnapshot[];

    public displaySize: Point;

    constructor(layout: Layout) {
        this.createSnapshot(layout);
    }

    public restoreLayout(layout: Layout): void {
        layout.cells.length = 0;
        layout.edges.length = 0;

        const newCellById: {[cellId: string]: Cell} = {};
        const newEdgeById: {[edgeId: string]: Edge} = {};

        this.edges.forEach((edgeSnapshot: EdgeSnapshot) =>
            this.restoreEdge(layout, edgeSnapshot, newEdgeById)
        );
        this.cells.forEach((cellSnapshot: CellSnapshot) =>
            this.restoreCell(layout, cellSnapshot, newCellById)
        );
        this.bindCellsAndEdges(newCellById, newEdgeById);

        console.log(this, layout);
    }

    private createSnapshot(layout: Layout): void {
        this.size = layout.size;
        this.displaySize = layout.displaySize;

        const cellSnapshotById: {[cellId: string]: CellSnapshot} = {};
        const cellsSnapshot: CellSnapshot[] = layout.cells.map(cell => {
            const cellSnapshot: CellSnapshot = {
                cell,
                id:           cell.id,
                edges:        [null, null, null, null], // To be populated below.
                widgets:      cell.widgets.slice(),
                activeWidget: cell.activeWidget,
                display: {
                    pos:       cell.display.pos.slice() as Point,
                    nextPos:   cell.display.nextPos.slice() as Point,
                    size:      cell.display.size.slice() as Point,
                    wireframe: null
                }
            };

            cellSnapshotById[cell.id] = cellSnapshot;

            return cellSnapshot;
        });
        const edgesSnapshot: EdgeSnapshot[] = layout.edges.map(edge => {
            const edgeAxis: Dimension = edge.axis;
            const edgeSnapshot: EdgeSnapshot = {
                edge,
                id:            edge.id,
                axis:          edgeAxis,
                axisRange:     edge.axisRange.slice() as Point,
                movingAxis:    edge.movingAxis,
                movingAxisPos: edge.movingAxisPos,
                sides:         null, // To be populated below.
                isBoundary:    edge.isBoundary,
                display:       {
                    movingAxisPos: edge.display.movingAxisPos,
                    axisRange:     edge.display.axisRange.slice() as Point
                }
            };

            edgeSnapshot.sides = edge.sides.map((cells: Cell[], side: Side) =>
                cells.map(cell => {
                    const cellSnapshot: CellSnapshot = cellSnapshotById[cell.id];
                    const boxEdge: BoxEdge = BOX_EDGES_MAP[edgeAxis][side];

                    cellSnapshot.edges[boxEdge] = edgeSnapshot;

                    return cellSnapshot;
                })
            ) as Pair<CellSnapshot[]>;

            return edgeSnapshot;
        });

        this.cells = cellsSnapshot;
        this.edges = edgesSnapshot;
    }

    private restoreEdge(
        layout: Layout,
        edgeSnapshot: EdgeSnapshot,
        newEdgeById: {[edgeId: string]: Edge}
    ): void {
        let edge: Edge = edgeSnapshot.edge;

        if (edge) {
            edge.axis          = edgeSnapshot.axis;
            edge.axisRange     = edgeSnapshot.axisRange.slice() as Point;
            edge.movingAxis    = edgeSnapshot.movingAxis;
            edge.movingAxisPos = edgeSnapshot.movingAxisPos;
            edge.sides         = null; // To be populated in bindCellsAndEdges method.
            edge.isBoundary    = edgeSnapshot.isBoundary;
            edge.display.movingAxisPos = edgeSnapshot.display.movingAxisPos;
            edge.display.axisRange     = edgeSnapshot.display.axisRange.slice() as Point;
        } else {
            edge = {
                id:            edgeSnapshot.id,
                axis:          edgeSnapshot.axis,
                axisRange:     edgeSnapshot.axisRange.slice() as Point,
                movingAxis:    edgeSnapshot.movingAxis,
                movingAxisPos: edgeSnapshot.movingAxisPos,
                sides:         null, // To be populated in bindCellsAndEdges method.
                isBoundary:    edgeSnapshot.isBoundary,
                display:       {
                    movingAxisPos: edgeSnapshot.display.movingAxisPos,
                    axisRange:     edgeSnapshot.display.axisRange.slice() as Point
                }
            };
        }

        newEdgeById[edge.id] = edge;
        layout.edges.push(edge);
    }

    private restoreCell(
        layout: Layout,
        cellSnapshot: CellSnapshot,
        newCellById: {[cellId: string]: Cell}
    ): void {
        let cell: Cell = cellSnapshot.cell;
        const widgets: Widget[] = cellSnapshot.widgets.slice();

        if (cell) {
            cell.edges           = null; // To be populated in bindCellsAndEdges method.
            cell.widgets         = widgets;
            cell.activeWidget    = cellSnapshot.activeWidget;
            cell.display.pos     = cellSnapshot.display.pos.slice() as Point;
            cell.display.nextPos = cellSnapshot.display.nextPos.slice() as Point;
            cell.display.size    = cellSnapshot.display.size.slice() as Point;
        } else {
            cell = {
                id:           cellSnapshot.id,
                edges:        null, // To be populated in bindCellsAndEdges method.
                widgets,
                activeWidget: cellSnapshot.activeWidget,
                display: {
                    pos:       cellSnapshot.display.pos.slice() as Point,
                    nextPos:   cellSnapshot.display.nextPos.slice() as Point,
                    size:      cellSnapshot.display.size.slice() as Point,
                    wireframe: null
                }
            };
        }

        widgets.forEach(widget => widget.cell = cell);

        newCellById[cell.id] = cell;
        layout.cells.push(cell);
    }

    private bindCellsAndEdges(newCellById: {[cellId: string]: Cell}, newEdgeById: {[edgeId: string]: Edge}): void {
        this.cells.forEach(cellSnapshot => {
            newCellById[cellSnapshot.id].edges = cellSnapshot.edges.map(
                edgeSnapshot => newEdgeById[edgeSnapshot.id]
            ) as BoxEdges;
        });
        this.edges.forEach(edgeSnapshot => {
            newEdgeById[edgeSnapshot.id].sides = edgeSnapshot.sides.map(
                cellsSnapshot => cellsSnapshot.map(cellSnapshot => newCellById[cellSnapshot.id])
            ) as EdgeSides;
        });
    }

}
